// ═══════════════════════════════════════════════════════════════════════════════════════════════════
// agentModel.ts — typed API client for the per-agent model binding (ai.AiAgent.ModelId)
// ═══════════════════════════════════════════════════════════════════════════════════════════════════
//
// Backs the Model selection card on the agent detail page's AI Brain tab.
// Side-car API mirroring /api/AgentReasoning — four endpoints:
//
//   GET  /api/AgentModel/Agent/{agentId}              — current binding + capability flags
//   GET  /api/AgentModel/Options/{agentId}            — models this agent may switch to
//   POST /api/AgentModel/Agent/{agentId}/Set          — change model (audited)
//   GET  /api/AgentModel/Agent/{agentId}/History      — change history
//
// Every endpoint returns OperationResult<T> per project convention; this
// module unwraps and throws on failure so callers get a clean Promise<T>.
// ═══════════════════════════════════════════════════════════════════════════════════════════════════

import { apiGet, apiPost } from './client';
import type { OperationResult } from './types';

// ────────────────────────────────────────────────────────────────────────────
// Types — match the C# DTOs in AgentModelController.cs
// ────────────────────────────────────────────────────────────────────────────

export interface AgentModelState {
  agentId: number;
  agentDisplayName: string;
  agentMerchantId: number | null;

  /** Stored binding on ai.AiAgent.ModelId (NOT NULL, default 27). */
  modelId: number;
  modelName: string;
  modelTitle: string | null;
  deploymentName: string | null;
  modelActive: boolean;
  enableInChat: boolean;
  enableInProcess: boolean;
  supportsReasoningEffort: boolean;
  supportsVision: boolean;

  /** Provider (ai.API) the model is served through. */
  apiId: number | null;
  apiTitle: string | null;
  useFoundryAgent: boolean;

  /** Whether a Foundry variant of this agent exists for the bound model. */
  foundryVariantProvisioned: boolean;
  foundryVariantAgentRef: string | null;

  /** Server-side caveat about the CURRENT binding (inactive model, chat disabled, …). Null when clean. */
  warning: string | null;
}

export interface AgentModelOption {
  modelId: number;
  name: string;
  title: string | null;
  deploymentName: string | null;
  /** Null = platform-wide model; otherwise scoped to that merchant. */
  merchantId: number | null;
  enableInChat: boolean;
  enableInProcess: boolean;
  supportsReasoningEffort: boolean;
  supportsVision: boolean;
  apiId: number | null;
  apiTitle: string | null;
  useFoundryAgent: boolean;
  foundryVariantProvisioned: boolean;
  /** True for the model the agent is bound to right now. */
  isCurrent: boolean;
}

export interface SetAgentModelRequest {
  newModelId: number;
  reason?: string;
}

export interface SetAgentModelResult {
  changed: boolean;
  oldModelId: number;
  newModelId: number;
  /** Non-blocking caveat about the NEW binding. Surface it; never swallow it. */
  warning: string | null;
  /** True when a Foundry variant could be provisioned for the new model. */
  canProvision: boolean;
  /** Fresh state after the write — saves a second round-trip. */
  state: AgentModelState;
}

export interface AgentModelHistoryRow {
  id: number;
  oldModelId: number | null;
  oldModelName: string | null;
  newModelId: number;
  newModelName: string;
  reason: string | null;
  changedByUserId: number;
  changedAtUtc: string;            // ISO timestamp
}

// ────────────────────────────────────────────────────────────────────────────
// API calls
// ────────────────────────────────────────────────────────────────────────────

/** Current model binding + capability flags for one agent. */
export async function getAgentModel(agentId: number): Promise<AgentModelState> {
  const res = await apiGet<OperationResult<AgentModelState>>(`/api/AgentModel/Agent/${agentId}`);
  if (!res.isSuccess || !res.data) throw new Error(res.message || `Failed to load model for agent ${agentId}`);
  return res.data;
}

/** Models this agent is allowed to switch to (platform + its merchant's). */
export async function getAgentModelOptions(agentId: number): Promise<AgentModelOption[]> {
  const res = await apiGet<OperationResult<AgentModelOption[]>>(`/api/AgentModel/Options/${agentId}`);
  if (!res.isSuccess) throw new Error(res.message || `Failed to load model options for agent ${agentId}`);
  return res.data ?? [];
}

/**
 * Re-bind the agent to another model. Writes one audit row. Returns the
 * freshly-loaded state plus any non-blocking warning about the new binding.
 */
export async function setAgentModel(
  agentId: number,
  req: SetAgentModelRequest,
): Promise<SetAgentModelResult> {
  const res = await apiPost<OperationResult<SetAgentModelResult>>(
    `/api/AgentModel/Agent/${agentId}/Set`,
    req,
  );
  if (!res.isSuccess || !res.data) throw new Error(res.message || 'Failed to update model');
  return res.data;
}

/** History of model changes for one agent, newest first. Drives the audit drawer. */
export async function getAgentModelHistory(
  agentId: number,
  take: number = 50,
): Promise<AgentModelHistoryRow[]> {
  const res = await apiGet<OperationResult<AgentModelHistoryRow[]>>(
    `/api/AgentModel/Agent/${agentId}/History`,
    { take },
  );
  if (!res.isSuccess) throw new Error(res.message || 'Failed to load model history');
  return res.data ?? [];
}
